import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["minValue", "maxValue", "valueLabels", "step", "leftValue", "rightValue", "className", "disabled", "onChange", "onDragEnd"];
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
var getRangeValues = function getRangeValues(valueLeft, valueRight) {
  var finalLeftValue = valueRight <= valueLeft ? valueRight : valueLeft;
  var finalRightValue = valueLeft >= valueRight ? valueLeft : valueRight;
  return [finalLeftValue, finalRightValue];
};
var RangeSlider = function RangeSlider(props) {
  var minValue = props.minValue,
    maxValue = props.maxValue,
    valueLabels = props.valueLabels,
    step = props.step,
    leftValue = props.leftValue,
    rightValue = props.rightValue,
    className = props.className,
    disabled = props.disabled,
    onChange = props.onChange,
    onDragEnd = props.onDragEnd,
    remainingProps = _objectWithoutProperties(props, _excluded);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    activeLeft = _useState2[0],
    setActiveLeft = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    activeRight = _useState4[0],
    setActiveRight = _useState4[1];
  var _useState5 = useState(leftValue ? leftValue : minValue),
    _useState6 = _slicedToArray(_useState5, 2),
    valueLeft = _useState6[0],
    setValueLeft = _useState6[1];
  var _useState7 = useState(rightValue ? rightValue : maxValue),
    _useState8 = _slicedToArray(_useState7, 2),
    valueRight = _useState8[0],
    setValueRight = _useState8[1];
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    isChangingLeft = _useState10[0],
    setIsChangingLeft = _useState10[1];
  var _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    isChangingRight = _useState12[0],
    setIsChangingRight = _useState12[1];
  useEffect(function () {
    if (valueLeft >= minValue && valueLeft <= maxValue && valueRight >= minValue && valueRight <= maxValue) {
      setValueLeft(valueLeft);
      setValueRight(valueRight);
    }
  }, [valueLeft, valueRight, minValue, maxValue]);
  var changeValues = function changeValues(newValues) {
    var newValueLeft = newValues.valueLeft ? newValues.valueLeft : valueLeft;
    var newValueRight = newValues.valueRight ? newValues.valueRight : valueRight;
    setValueLeft(newValueLeft);
    setValueRight(newValueRight);
    setIsChangingLeft(!!newValues.valueLeft);
    setIsChangingRight(!!newValues.valueRight);
    var _getRangeValues = getRangeValues(newValueLeft, newValueRight),
      _getRangeValues2 = _slicedToArray(_getRangeValues, 2),
      newLeftValue = _getRangeValues2[0],
      newRightValue = _getRangeValues2[1];
    onChange(newLeftValue, newRightValue);
  };
  var handleChangeValueLeft = function handleChangeValueLeft(event) {
    return changeValues({
      valueLeft: parseInt(event.target.value, 10)
    });
  };
  var handleChangeValueRight = function handleChangeValueRight(event) {
    return changeValues({
      valueRight: parseInt(event.target.value, 10)
    });
  };
  var onMouseDownLeft = function onMouseDownLeft() {
    setIsChangingLeft(true);
    setIsChangingRight(false);
  };
  var onMouseDownRight = function onMouseDownRight() {
    setIsChangingLeft(false);
    setIsChangingRight(true);
  };
  var getTrackMiddlePosition = function getTrackMiddlePosition(targetWidth) {
    var trackValueLeftRelative = (valueLeft - minValue) / (maxValue - minValue);
    var trackValueRightRelative = (valueRight - minValue) / (maxValue - minValue);
    var trackMiddleRelative = (trackValueLeftRelative + trackValueRightRelative) / 2;
    return trackMiddleRelative * targetWidth;
  };
  var onMouseMoveLeft = function onMouseMoveLeft(event) {
    if (!isChangingLeft) {
      var target = event.target.getBoundingClientRect();
      var mouseHover = event.clientX - target.left;
      var targetWidth = event.target.offsetWidth;
      var trackMiddlePosition = getTrackMiddlePosition(targetWidth);
      if (valueRight > valueLeft && mouseHover > trackMiddlePosition) {
        setActiveLeft(false);
        setActiveRight(true);
      }
      if (valueRight < valueLeft && mouseHover < trackMiddlePosition) {
        setActiveLeft(false);
        setActiveRight(true);
      }
    }
  };
  var onMouseMoveRight = function onMouseMoveRight(event) {
    if (!isChangingRight) {
      var target = event.target.getBoundingClientRect();
      var mouseHover = event.clientX - target.left;
      var targetWidth = event.target.offsetWidth;
      var trackMiddlePosition = getTrackMiddlePosition(targetWidth);
      if (valueRight > valueLeft && mouseHover < trackMiddlePosition) {
        setActiveLeft(true);
        setActiveRight(false);
      }
      if (valueRight < valueLeft && mouseHover > trackMiddlePosition) {
        setActiveLeft(true);
        setActiveRight(false);
      }
    }
  };
  var onMouseUpLeft = function onMouseUpLeft() {
    setIsChangingLeft(false);
    setIsChangingRight(false);
    var _getRangeValues3 = getRangeValues(valueLeft, valueRight),
      _getRangeValues4 = _slicedToArray(_getRangeValues3, 2),
      newLeftValue = _getRangeValues4[0],
      newRightValue = _getRangeValues4[1];
    onDragEnd(newLeftValue, newRightValue);
  };
  var onMouseUpRight = function onMouseUpRight() {
    setIsChangingLeft(false);
    setIsChangingRight(false);
    var _getRangeValues5 = getRangeValues(valueLeft, valueRight),
      _getRangeValues6 = _slicedToArray(_getRangeValues5, 2),
      newLeftValue = _getRangeValues6[0],
      newRightValue = _getRangeValues6[1];
    onDragEnd(newLeftValue, newRightValue);
  };
  var _getRangeValues7 = getRangeValues(valueLeft, valueRight),
    _getRangeValues8 = _slicedToArray(_getRangeValues7, 2),
    finalLeftValue = _getRangeValues8[0],
    finalRightValue = _getRangeValues8[1];
  var trackWidth = 100 * (finalRightValue - finalLeftValue) / (maxValue - minValue);
  var trackLeft = 100 * (finalLeftValue - minValue) / (maxValue - minValue);
  var wrapperClassNames = classNames('range-slider', disabled && 'disabled', valueLabels && 'show-value-labels', className && className);
  return /*#__PURE__*/React.createElement("div", _extends({}, remainingProps, {
    className: wrapperClassNames
  }), /*#__PURE__*/React.createElement("div", {
    className: "range-whole-track"
  }), /*#__PURE__*/React.createElement("div", {
    className: "range-track",
    style: {
      width: "".concat(trackWidth, "%"),
      left: "".concat(trackLeft, "%")
    }
  }, valueLabels && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: "range-value"
  }, finalLeftValue), /*#__PURE__*/React.createElement("div", {
    className: "range-value"
  }, finalRightValue))), /*#__PURE__*/React.createElement("input", {
    className: "".concat(isChangingLeft ? 'changing' : ''),
    style: {
      zIndex: activeLeft ? '6' : '5'
    },
    value: valueLeft,
    min: minValue,
    max: maxValue,
    step: step,
    type: "range",
    onChange: handleChangeValueLeft,
    onMouseDown: onMouseDownLeft,
    onMouseMove: onMouseMoveLeft,
    onMouseUp: onMouseUpLeft
  }), /*#__PURE__*/React.createElement("input", {
    className: "".concat(isChangingRight ? 'changing' : ''),
    style: {
      zIndex: activeRight ? '6' : '5'
    },
    value: valueRight,
    min: minValue,
    max: maxValue,
    step: step,
    type: "range",
    onChange: handleChangeValueRight,
    onMouseDown: onMouseDownRight,
    onMouseMove: onMouseMoveRight,
    onMouseUp: onMouseUpRight
  }));
};
RangeSlider.defaultProps = {
  step: 1,
  minValue: 0,
  maxValue: Number.MAX_VALUE,
  valueLabels: false,
  disabled: false,
  onChange: function onChange() {},
  onDragEnd: function onDragEnd() {}
};
RangeSlider.propTypes = {
  leftValue: PropTypes.number,
  rightValue: PropTypes.number,
  minValue: PropTypes.number.isRequired,
  maxValue: PropTypes.number.isRequired,
  valueLabels: PropTypes.bool,
  step: PropTypes.number,
  onChange: PropTypes.func,
  onDragEnd: PropTypes.func,
  className: PropTypes.string,
  disabled: PropTypes.bool
};
export default RangeSlider;